import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth";
import { useDevices } from "../devices";
import { Link2, CheckCircle2, Trash2 } from "lucide-react";

export default function LinkDevice() {
  const { t, formatApiError } = useAuth();
  const { devices, reload, deleteDevice, setSelectedDeviceId } = useDevices();
  const nav = useNavigate();
  const [deviceId, setDeviceId] = useState("");
  const [carName, setCarName] = useState("My Car");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const id = deviceId.trim();
      await api.post("/device/link", { device_id: id, name: carName });
      await reload();
      setSelectedDeviceId(id);
      setOk(true);
      setTimeout(() => nav("/home", { replace: true }), 900);
    } catch (er) {
      setErr(formatApiError(er));
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async (d) => {
    if (!window.confirm(`${t.confirmDelete || "Are you sure?"}\n${d.name} (${d.device_id})`)) return;
    setDeletingId(d.device_id);
    try {
      await deleteDevice(d.device_id);
    } catch (er) {
      setErr(formatApiError(er));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div data-testid="link-screen" className="min-h-screen bg-white flex flex-col pb-24">
      <div className="px-6 pt-12 pb-4">
        <div className="w-14 h-14 rounded-2xl bg-primary-light flex items-center justify-center text-primary">
          <Link2 size={26} />
        </div>
        <h1 className="mt-4 text-3xl font-black text-slate-900">{t.linkDevice}</h1>
        <p className="mt-2 text-slate-500">{t.deviceIdHelp}</p>
      </div>

      <form onSubmit={submit} className="px-6 mt-4 space-y-4">
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{t.deviceId}</span>
          <input
            data-testid="link-device-id"
            value={deviceId}
            onChange={(e) => setDeviceId(e.target.value)}
            placeholder="CT-DEMO-0001"
            required
            className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-3 text-base font-mono tracking-wider outline-none focus:border-primary"
          />
        </label>
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{t.carName}</span>
          <input
            data-testid="link-car-name"
            value={carName}
            onChange={(e) => setCarName(e.target.value)}
            className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-3 text-base outline-none focus:border-primary"
          />
        </label>

        {err && <div data-testid="link-error" className="text-sm text-parked bg-red-50 rounded-lg px-3 py-2">{err}</div>}
        {ok && (
          <div data-testid="link-success" className="flex items-center gap-2 text-moving bg-green-50 rounded-lg px-3 py-2">
            <CheckCircle2 size={18} /> {t.linked}
          </div>
        )}

        <button
          data-testid="link-submit"
          disabled={loading}
          className="w-full py-3.5 rounded-xl bg-primary hover:bg-primary-hover text-white font-bold text-base active:scale-[0.98] disabled:opacity-60"
        >
          {loading ? t.loading : t.link}
        </button>

        {devices.length > 0 && (
          <div className="mt-6">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Linked devices</p>
            <div className="space-y-2" data-testid="linked-devices-list">
              {devices.map((d) => (
                <div
                  key={d.device_id}
                  data-testid={`linked-device-${d.device_id}`}
                  className="flex items-center justify-between border border-slate-200 rounded-xl px-3 py-2"
                >
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{d.name}</div>
                    <div className="text-xs font-mono text-slate-500">{d.device_id}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      data-testid={`open-device-${d.device_id}`}
                      onClick={() => { setSelectedDeviceId(d.device_id); nav("/home"); }}
                      className="text-primary font-semibold text-sm px-2 py-1"
                    >
                      Open →
                    </button>
                    <button
                      type="button"
                      data-testid={`delete-device-${d.device_id}`}
                      onClick={() => onDelete(d)}
                      disabled={deletingId === d.device_id}
                      title={t.delete || "Delete"}
                      className="p-2 rounded-lg text-parked hover:bg-red-50 disabled:opacity-50"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
